// Stencil to pixels and to paper. Pure functions: the only DOM here is the context you hand in.

import type { CarveTool, Part, Stencil, Stroke } from '../types';
import { bounds, partWorldPolys, polysToD, type Pt } from './geom';
import { traceField } from './regions';

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export interface Shape {
  tool: CarveTool;
  polys: Pt[][];       // filled rings, or for a line the single polyline
  line: boolean;
  width: number;
}

const MID = 0.02;

/**
 * The stencil as drawn: with the mirror on, the right half is the left half's reflection, so anything whose middle is
 * right of the line is hidden and everything else is drawn twice
 */
export function mirrored(stencil: Stencil): { parts: Part[]; strokes: Stroke[] } {
  if (!stencil.mirror) return stencil;
  const parts: Part[] = [], strokes: Stroke[] = [];
  for (const p of stencil.parts) {
    if (p.real) { parts.push(p); continue; }
    if (p.x > MID) continue;
    parts.push(p);
    if (p.x < -MID) parts.push(reflectPart(p));
  }
  for (const s of stencil.strokes) {
    if (!s.points.length) continue;
    if (s.real) { strokes.push(s); continue; }
    const b = bounds([s.points as Pt[]]), cx = (b.minX + b.maxX) / 2;
    if (cx > MID) continue;
    strokes.push(s);
    if (b.minX < -MID) strokes.push(reflectStroke(s));
  }
  return { parts, strokes };
}

// x to -x: a part's flip toggles and its turn reverses
export const reflectPart = (p: Part): Part => ({ ...p, id: p.id + '~', x: -p.x, rotate: -p.rotate, flip: !p.flip });
export const reflectStroke = (s: Stroke): Stroke => ({ ...s, id: s.id + '~', points: s.points.map(([x, y]) => [-x, y] as [number, number]), ...(s.holes ? { holes: s.holes.map(h => h.map(([x, y]) => [-x, y] as [number, number])) } : {}) });

// Parts first, then strokes in order. A closed stroke is a filled shape unless it is an outline; a closed eraser is a white cutout. Pencil is left out:
// it is for the drawer, not the pumpkin.
export function pathsForStencil(stencil: Stencil, opts: { pencil?: boolean } = {}): Shape[] {
  const out: Shape[] = [];
  const st = mirrored(stencil);
  for (const p of st.parts) out.push({ tool: p.tool, polys: partWorldPolys(p), line: false, width: 0 });
  for (const s of st.strokes) {
    if (s.tool === 'pencil' && !opts.pencil) continue;
    if (!s.points.length) continue;
    const fill = s.closed && !s.outline && s.points.length > 2;
    const pts = s.points as Pt[];
    out.push({ tool: s.tool, polys: [s.outline ? [...pts, pts[0]] : pts, ...(fill && s.holes ? s.holes as Pt[][] : [])], line: !fill, width: s.width });
  }
  return out;
}

function trace(ctx: Ctx, shape: Shape) {
  ctx.beginPath();
  for (const poly of shape.polys) {
    poly.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    if (!shape.line) ctx.closePath();
  }
}

/** old stencils had no `cut`: everything on them was meant to be carved */
export const isCut = (st: Stencil) => st.cut !== false;

type Canv = HTMLCanvasElement | OffscreenCanvas;
export const makeCanvas = (n: number): Canv => {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(n, n);
  const c = document.createElement('canvas');
  c.width = c.height = n;
  return c;
};

/**
 * Where the stencil has ink, as alpha: its filled shapes and stroked lines, scraped or cut alike, the eraser taking
 * them back out. Pencil is not ink. This is what "stay within the lines" keeps a carve to.
 */
export function coverage(canvas: Canv, stencil: Stencil, size: number): Canv {
  const c = canvas.getContext('2d') as Ctx;
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.globalCompositeOperation = 'source-over';
  c.clearRect(0, 0, size, size);
  c.setTransform(size / 2, 0, 0, -size / 2, size / 2, size / 2);
  c.lineCap = 'round';
  c.lineJoin = 'round';
  c.fillStyle = c.strokeStyle = '#fff';
  for (const shape of pathsForStencil(stencil)) {
    c.globalCompositeOperation = shape.tool === 'eraser' ? 'destination-out' : 'source-over';
    trace(c, shape);
    if (shape.line) { c.lineWidth = shape.width; c.stroke(); } else c.fill('nonzero');
  }
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.globalCompositeOperation = 'source-over';
  return canvas;
}

const DASH_FIELD = 512;
let dashField: Canv | null = null;

/**
 * The stencil's outline as a dashed line, white on black: what Show stencil puts on the skin while carving with the
 * tools. The ink is traced back into rings and each is stroked with real dashes, so they run along the line round any
 * bend and stay put.
 */
export function drawDashes(stencil: Stencil, ctx: Ctx, size: number): void {
  const n = DASH_FIELD;
  // read back each time: the context has to be asked for that way before coverage() first draws on it
  if (!dashField) { dashField = makeCanvas(n); dashField.getContext('2d', { willReadFrequently: true }); }
  const fc = coverage(dashField, { ...stencil, carve: [] }, n).getContext('2d') as Ctx;
  const px = fc.getImageData(0, 0, n, n).data, f = new Float32Array(n * n);
  for (let i = 0; i < f.length; i++) f[i] = px[i * 4 + 3] / 255;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, size, size);
  ctx.setTransform(size / 2, 0, 0, -size / 2, size / 2, size / 2);
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = DASH.width;
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'round';
  ctx.setLineDash([DASH.on, DASH.off]);
  for (const r of traceField(f, n, 0.0008)) {
    for (const ring of [r.outer, ...r.holes]) {
      // short rings get their dash pitch stretched to a whole number, so no stub where the line meets its start
      let len = 0;
      for (let i = 0; i < ring.length; i++) { const a = ring[i], b = ring[(i + 1) % ring.length]; len += Math.hypot(b[0] - a[0], b[1] - a[1]); }
      const k = len / Math.max(1, Math.round(len / (DASH.on + DASH.off))) / (DASH.on + DASH.off);
      ctx.setLineDash([DASH.on * k, DASH.off * k]);
      ctx.beginPath();
      ring.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      ctx.closePath();
      ctx.stroke();
    }
  }
  ctx.restore();
}
/** in sheet units, the sheet being 2 across */
export const DASH = { on: 0.03, off: 0.022, width: 0.013 };

// ---- printable sheet

const f = (n: number) => n.toFixed(3);
const flip = (p: Pt) => `${f(p[0])} ${f(-p[1])}`;

function shapeSVG(s: Shape, colour: string): string {
  if (s.line) {
    const d = 'M' + s.polys[0].map(flip).join('L');
    return `<path d="${d}" fill="none" stroke="${colour}" stroke-width="${f(s.width)}" stroke-linecap="round" stroke-linejoin="round"/>`;
  }
  return `<path d="${polysToD(s.polys, true)}" fill="${colour}" fill-rule="nonzero"/>`;
}

// Cut areas solid black, scraped areas a grey hatch. Each is a mask built in drawing order, so an eraser stroke
// restores skin from both and a cut always wins over a scrape, exactly as in the mask texture.
export function stencilToSVG(stencil: Stencil, opts: { sheet?: boolean } = {}): string {
  const sheet = opts.sheet !== false;
  const shapes = pathsForStencil(stencil);
  const mask = (id: string, wants: (t: CarveTool) => boolean) =>
    `<mask id="${id}" maskUnits="userSpaceOnUse" x="-1" y="-1" width="2" height="2"><rect x="-1" y="-1" width="2" height="2" fill="#000"/>`
    + shapes.map(s => (s.tool === 'eraser' ? shapeSVG(s, '#000') : wants(s.tool) ? shapeSVG(s, '#fff') : '')).join('')
    + '</mask>';
  const art = `<rect x="-1" y="-1" width="2" height="2" fill="url(#hatch)" mask="url(#scrape)"/>`
    + `<rect x="-1" y="-1" width="2" height="2" fill="#000" mask="url(#cut)"/>`;
  const defs = `<defs>
<pattern id="hatch" width="0.05" height="0.05" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="0.05" height="0.05" fill="#fff"/><rect width="0.02" height="0.05" fill="#7a7a7a"/></pattern>
${mask('cut', t => t !== 'scraper')}
${mask('scrape', t => t === 'scraper')}
</defs>`;
  if (!sheet) return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-1 -1 2 2" width="800" height="800">${defs}${art}</svg>`;

  const font = 'font-family="Courier New, monospace" fill="#222"';
  const key = `<g transform="translate(-0.95 1.2)">
<rect x="0" y="0" width="0.14" height="0.14" fill="#000"/><text x="0.2" y="0.1" font-size="0.085" ${font}>CUT HERE (saw it out)</text>
<rect x="0" y="0.2" width="0.14" height="0.14" fill="url(#hatch)" stroke="#555" stroke-width="0.008"/><text x="0.2" y="0.3" font-size="0.085" ${font}>SCRAPE HERE (shave, don't cut through)</text>
<rect x="0" y="0.4" width="0.14" height="0.14" fill="#fff" stroke="#555" stroke-width="0.008"/><text x="0.2" y="0.5" font-size="0.085" ${font}>LEAVE ALONE</text>
</g>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-1.25 -1.3 2.5 3.1" width="190mm" height="235mm">
${defs}
<rect x="-1.25" y="-1.3" width="2.5" height="3.1" fill="#fff"/>
<g fill="none" stroke="#bbb" stroke-width="0.012">
<path d="M0 -0.84C0.08 -0.95 0.1 -1.05 0.16 -1.12" stroke-linecap="round" stroke-width="0.04"/>
<ellipse cx="0" cy="0.02" rx="0.97" ry="0.84"/><path d="M-0.35 -0.8C-0.5 0 -0.4 0.7 -0.2 0.86M0.35 -0.8C0.5 0 0.4 0.7 0.2 0.86" stroke-dasharray="0.03 0.03"/>
</g>
${art}
<rect x="-1" y="-1" width="2" height="2" fill="none" stroke="#999" stroke-width="0.008" stroke-dasharray="0.04 0.03"/>
<text x="-1" y="-1.06" font-size="0.075" ${font}>STENCIL SHEET${stencil.name ? ' - ' + stencil.name.toUpperCase().replace(/[<>&"]/g, '') : ''}</text>
<text x="1" y="-1.06" font-size="0.065" text-anchor="end" ${font}>TOP</text>
${key}
</svg>`;
}
